import React, { useState, useEffect } from 'react'
import ProjectFolderIcon from '../../assets/ProjectFolderIcon'
import { FolderIcon } from '@heroicons/react/24/outline'
import { SideMenuParentArrowIcon } from '@sistemiv/s-components'
import classNames from 'classnames'
import { Link, useMatch, useParams } from 'react-router-dom'

type Folder = {
  folders: Folder[]
  name: string
  totalSize: number
  lastModified: string
}

type Project = Folder

type Props = {
  projects: Project[]
}

export default function SideMenuList({ projects }: Props) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [selected, setSelected] = useState('')
  const match = useMatch('/:org/:project/*')
  const rest = match?.params['*'] || ''
  const project = match?.params['project'] || ''

  useEffect(() => {
    if (rest) {
      const fullPath = `/${project}/${rest}`
      setSelected(fullPath)
      const parts = fullPath.split('/').filter(Boolean)

      const pathsToExpand = parts.map((_, i) => '/' + parts.slice(0, i + 1).join('/'))

      setExpanded((prev) => {
        const newExpanded = { ...prev }
        for (const path of pathsToExpand) {
          newExpanded[path] = true
        }
        return newExpanded
      })
    } else if (project) {
      setSelected(`/${project}`)
      setExpanded((prev) => ({
        ...prev,
        [`/${project}`]: true,
      }))
    }
  }, [rest, project, setExpanded])

  const toggleExpand = (path: string) => {
    setExpanded((prev) => ({
      ...prev,
      [path]: !prev[path],
    }))
  }

  const RenderFolder = (folder: Folder, parentPath: string, isProjectRoot = false) => {
    const fullPath = `${parentPath}/${folder.name}`
    const isExpanded = expanded[fullPath]
    const hasSubfolders = folder.folders.length > 0
    const { org } = useParams()

    return (
      <div key={fullPath} className='w-full'>
        <div
          className={classNames(
            'w-full flex items-center space-x-2 py-2 px-1 hover:!bg-sky-50',
            isProjectRoot && 'pl-4',
            selected === fullPath && 'bg-sky-100 hover:!bg-sky-100 text-blue-500',
          )}
        >
          {hasSubfolders && (
            <button onClick={() => toggleExpand(fullPath)} className='text-gray-600 hover:text-black'>
              <SideMenuParentArrowIcon className={classNames('w-3 h-3 fill-black', isExpanded && 'rotate-90')} />
            </button>
          )}

          <Link to={`/${org}${fullPath}`} className='flex cursor-pointer items-center'>
            {isProjectRoot ? (
              <ProjectFolderIcon className='w-5 h-5 mr-2' fill='#636A7E' />
            ) : (
              <FolderIcon className='w-5 h-5 mr-2' />
            )}
            {folder.name}
          </Link>
        </div>

        {isExpanded && hasSubfolders && (
          <div className='ml-6'>{folder.folders.map((subFolder) => RenderFolder(subFolder, fullPath))}</div>
        )}
      </div>
    )
  }

  return <div className='space-y-2'>{projects.map((project) => RenderFolder(project, '', true))}</div>
}
